/**
 * Multiplayer HUD: connection pill, scoreboard, death screen, chat.
 *
 * `ui` owns the game HUD and its stylesheet, so nothing here touches either.
 * This is a separate overlay with its own `net-` prefixed styles, sitting on
 * the same DOM host. It follows the same rules as the rest of the UI: no CSS
 * transitions or keyframes (the capture harness has to be able to freeze a
 * frame), and no DOM writes unless a value actually changed — a `textContent`
 * assignment with an unchanged string still invalidates layout in Chromium.
 */

import * as THREE from 'three';

const CSS = `
.net-root{position:fixed;inset:0;pointer-events:none;z-index:60;
  font:500 12px/1.35 ui-monospace,"SF Mono",Menlo,Consolas,monospace;
  color:#e8eef4;-webkit-font-smoothing:antialiased}
.net-pill{position:absolute;top:10px;left:12px;display:flex;gap:8px;align-items:center;
  padding:5px 9px;background:rgba(8,11,14,.62);border:1px solid rgba(255,255,255,.09);
  border-radius:3px;letter-spacing:.06em;font-size:10.5px;text-transform:uppercase}
.net-dot{width:6px;height:6px;border-radius:50%;background:#4ad07a;box-shadow:0 0 6px #4ad07a}
.net-dot.bad{background:#e2564a;box-shadow:0 0 6px #e2564a}
.net-dot.warn{background:#ffc247;box-shadow:0 0 6px #ffc247}
.net-pill b{font-weight:600;color:#fff;letter-spacing:.04em}
.net-pill span{color:rgba(232,238,244,.62)}

.net-board{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
  width:min(760px,92vw);background:rgba(6,9,12,.92);border:1px solid rgba(255,255,255,.1);
  border-radius:4px;padding:0 0 10px;display:none;box-shadow:0 24px 80px rgba(0,0,0,.6)}
.net-board.on{display:block}
.net-bhead{display:flex;justify-content:space-between;align-items:baseline;
  padding:11px 16px;border-bottom:1px solid rgba(255,255,255,.09);
  letter-spacing:.14em;font-size:11px;text-transform:uppercase;color:rgba(232,238,244,.7)}
.net-bscore{font-size:19px;letter-spacing:.06em;color:#fff;font-weight:600}
.net-bscore i{font-style:normal;color:rgba(232,238,244,.35);margin:0 8px}
.net-t0{color:#5fb0ff}.net-t1{color:#ff9d5c}
.net-cols{display:grid;grid-template-columns:1.6fr 44px 42px 42px 58px 52px;gap:0;
  padding:7px 16px 5px;font-size:10px;letter-spacing:.12em;text-transform:uppercase;
  color:rgba(232,238,244,.4)}
.net-cols span:not(:first-child),.net-row span:not(:first-child){text-align:right}
.net-row{display:grid;grid-template-columns:1.6fr 44px 42px 42px 58px 52px;gap:0;
  padding:4px 16px;font-size:12.5px;border-left:2px solid transparent}
.net-row.me{background:rgba(255,194,71,.09);border-left-color:#ffc247}
.net-row.dead span:first-child{opacity:.45}
.net-row span:first-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.net-sep{height:1px;background:rgba(255,255,255,.07);margin:6px 16px}

.net-nameplate{position:absolute;left:0;top:0;display:flex;align-items:center;gap:6px;
  padding:3px 6px;background:rgba(5,8,11,.7);border:1px solid rgba(255,255,255,.12);
  transform:translate(-50%,-100%);white-space:nowrap;font-size:10px;letter-spacing:.08em;
  text-shadow:0 1px 2px #000;box-shadow:0 2px 8px rgba(0,0,0,.28)}
.net-nameplate[hidden]{display:none}
.net-nameplate b{color:#ffc247;font-size:9px;font-weight:700}
.net-nameplate span{max-width:150px;overflow:hidden;text-overflow:ellipsis}

.net-death{position:absolute;top:23%;left:0;right:0;text-align:center;display:none}
.net-death.on{display:block}
.net-death .k{font-size:12px;letter-spacing:.24em;color:rgba(232,238,244,.6);
  text-transform:uppercase}
.net-death .n{font-size:30px;letter-spacing:.05em;color:#fff;margin-top:6px;font-weight:600}
.net-death .s{margin-top:12px;font-size:12px;letter-spacing:.16em;color:#ffc247;
  text-transform:uppercase}

.net-chat{position:absolute;left:14px;bottom:120px;width:min(420px,42vw)}
.net-chat p{margin:0 0 3px;padding:2px 6px;background:rgba(8,11,14,.5);border-radius:2px;
  font-size:12px;line-height:1.3;overflow-wrap:anywhere}
.net-chat p b{font-weight:600}
.net-input{display:none;margin-top:4px;padding:4px 7px;background:rgba(8,11,14,.86);
  border:1px solid rgba(255,194,71,.5);border-radius:2px;font-size:12.5px;color:#fff}
.net-input.on{display:block}
.net-input i{font-style:normal;color:#ffc247;letter-spacing:.1em;margin-right:6px}
.net-caret{display:inline-block;width:7px;background:#ffc247;color:transparent}
`;

const CHAT_LINES = 6;
const CHAT_LIFE = 12;

let styleTag = null;

function ensureStyles() {
  if (styleTag?.isConnected) return;
  styleTag = document.createElement('style');
  styleTag.id = 'net-styles';
  styleTag.textContent = CSS;
  document.head.appendChild(styleTag);
}

function el(tag, cls, parent, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  if (parent) parent.appendChild(n);
  return n;
}

export class NetHud {
  constructor() {
    ensureStyles();
    const host = document.getElementById('ui') ?? document.body;
    this.root = el('div', 'net-root', host);
    this._nameplates = new Map();
    this._projected = new THREE.Vector3();
    this._nameplateFrame = 0;

    // ---- connection pill --------------------------------------------------
    this.pill = el('div', 'net-pill', this.root);
    this.dot = el('i', 'net-dot', this.pill);
    this.pillText = el('b', null, this.pill, 'CONNECTING');
    this.pillSub = el('span', null, this.pill, '');
    this._pillCache = '';
    this._subCache = '';
    this._dotCache = '';

    // ---- scoreboard -------------------------------------------------------
    this.board = el('div', 'net-board', this.root);
    const head = el('div', 'net-bhead', this.board);
    this.boardMode = el('div', null, head, 'TEAM DEATHMATCH');
    this.boardScore = el('div', 'net-bscore', head);
    this.scoreA = el('em', 'net-t0', this.boardScore, '0');
    this.scoreA.style.fontStyle = 'normal';
    el('i', null, this.boardScore, '—');
    this.scoreB = el('em', 'net-t1', this.boardScore, '0');
    this.scoreB.style.fontStyle = 'normal';
    this.boardTime = el('div', null, head, '10:00');

    const cols = el('div', 'net-cols', this.board);
    el('span', null, cols, 'Operator');
    el('span', null, cols, 'Lvl');
    el('span', null, cols, 'K');
    el('span', null, cols, 'D');
    el('span', null, cols, 'Score');
    el('span', null, cols, 'Life');
    this.rowHost = el('div', null, this.board);
    /** Pooled rows — the scoreboard redraws on every kill. */
    this.rows = [];

    // ---- death screen -----------------------------------------------------
    this.death = el('div', 'net-death', this.root);
    el('div', 'k', this.death, 'Killed by');
    this.killerName = el('div', 'n', this.death, '');
    this.respawnText = el('div', 's', this.death, '');
    this._respawnCache = '';

    // ---- chat -------------------------------------------------------------
    this.chat = el('div', 'net-chat', this.root);
    this.chatLines = [];
    for (let i = 0; i < CHAT_LINES; i++) {
      const p = el('p', null, this.chat);
      p.style.display = 'none';
      this.chatLines.push({ node: p, name: el('b', null, p, ''), text: el('span', null, p, ''), life: 0 });
    }
    this.input = el('div', 'net-input', this.chat);
    el('i', null, this.input, 'SAY');
    this.inputText = el('span', null, this.input, '');
    el('span', 'net-caret', this.input, '|');
  }

  /* ==================================================================== */

  setStatus(kind, text, sub) {
    if (this._dotCache !== kind) {
      this._dotCache = kind;
      this.dot.className = `net-dot${kind === 'ok' ? '' : ` ${kind}`}`;
    }
    if (this._pillCache !== text) {
      this._pillCache = text;
      this.pillText.textContent = text;
    }
    const s = sub ?? '';
    if (this._subCache !== s) {
      this._subCache = s;
      this.pillSub.textContent = s;
    }
  }

  setBoardVisible(on) {
    if (this.board.classList.contains('on') === on) return;
    this.board.classList.toggle('on', on);
  }

  /** @param {object[]} players roster, already sorted by score */
  drawBoard(players, teamScores, timeLeft, myId, mode) {
    this.scoreA.textContent = String(teamScores?.[0] ?? 0);
    this.scoreB.textContent = String(teamScores?.[1] ?? 0);
    const t = Math.max(0, timeLeft | 0);
    this.boardTime.textContent = `${(t / 60) | 0}:${String(t % 60).padStart(2, '0')}`;
    if (mode) this.boardMode.textContent = mode;

    while (this.rows.length < players.length) {
      const row = el('div', 'net-row', this.rowHost);
      const cells = [
        el('span', null, row, ''),
        el('span', null, row, ''),
        el('span', null, row, ''),
        el('span', null, row, ''),
        el('span', null, row, ''),
        el('span', null, row, ''),
      ];
      this.rows.push({ row, cells, cache: ['', '', '', '', '', '', ''] });
    }
    for (let i = 0; i < this.rows.length; i++) {
      const r = this.rows[i];
      const p = players[i];
      if (!p) {
        if (r.row.style.display !== 'none') r.row.style.display = 'none';
        continue;
      }
      if (r.row.style.display === 'none') r.row.style.display = '';
      const vals = [
        p.name,
        String(Math.max(1, p.level ?? 1)),
        String(p.kills ?? 0),
        String(p.deaths ?? 0),
        String(p.score ?? 0),
        p.alive === false && (p.respawns_left ?? 0) === 0
          ? 'OUT'
          : `${p.respawns_left ?? 5}R`,
      ];
      for (let c = 0; c < 6; c++) {
        if (r.cache[c] !== vals[c]) {
          r.cache[c] = vals[c];
          r.cells[c].textContent = vals[c];
        }
      }
      const cls = `net-row${p.id === myId ? ' me' : ''}${p.alive === false ? ' dead' : ''}`;
      if (r.cache[6] !== cls) {
        r.cache[6] = cls;
        r.row.className = cls;
      }
      const team = `net-t${(p.team ?? 0) & 1}`;
      if (r.cells[0].className !== team) r.cells[0].className = team;
    }
  }

  /** Project nearby online opponents' callsign and combat level above them. */
  drawNameplates(players, camera, width, height) {
    const frame = ++this._nameplateFrame;
    for (const [id, player] of players) {
      let plate = this._nameplates.get(id);
      if (!plate) {
        const node = el('div', 'net-nameplate', this.root);
        const level = el('b', null, node, 'LVL 1');
        const name = el('span', null, node, 'OPERATOR');
        plate = { node, level, name, x: NaN, y: NaN, levelText: '', nameText: '', frame: 0 };
        this._nameplates.set(id, plate);
      }
      plate.frame = frame;
      const visible = !!player.alive && !!camera && !!player.position;
      if (!visible) { plate.node.hidden = true; continue; }
      const p = this._projected.copy(player.position);
      p.y += 2.05;
      p.project(camera);
      if (p.z < -1 || p.z > 1 || Math.abs(p.x) > 1.1 || Math.abs(p.y) > 1.1) {
        plate.node.hidden = true;
        continue;
      }
      plate.node.hidden = false;
      const x = Math.round((p.x * 0.5 + 0.5) * width);
      const y = Math.round((-p.y * 0.5 + 0.5) * height);
      if (plate.x !== x || plate.y !== y) {
        plate.x = x; plate.y = y;
        plate.node.style.transform = `translate(${x}px,${y}px) translate(-50%,-100%)`;
      }
      const levelText = `LVL ${Math.max(1, player.info?.level ?? 1)}`;
      const nameText = player.name ?? 'OPERATOR';
      if (plate.levelText !== levelText) { plate.levelText = levelText; plate.level.textContent = levelText; }
      if (plate.nameText !== nameText) { plate.nameText = nameText; plate.name.textContent = nameText; }
    }
    for (const [id, plate] of this._nameplates) {
      if (plate.frame === frame) continue;
      plate.node.remove();
      this._nameplates.delete(id);
    }
  }

  showDeath(killer) {
    this.killerName.textContent = killer || 'THE MAP';
    this.death.classList.add('on');
  }

  setRespawnText(text) {
    if (this._respawnCache === text) return;
    this._respawnCache = text;
    this.respawnText.textContent = text;
  }

  hideDeath() {
    this.death.classList.remove('on');
    this._respawnCache = '';
  }

  /* ------------------------------------------------------------- chat -- */

  pushChat(name, text, colour) {
    // Scroll the pool: oldest line takes the new content and moves to the end.
    const line = this.chatLines.shift();
    this.chatLines.push(line);
    line.name.textContent = name ? `${name}: ` : '';
    line.name.style.color = colour ?? '#ffc247';
    line.text.textContent = text;
    line.life = CHAT_LIFE;
    line.node.style.display = '';
    for (const l of this.chatLines) this.chat.insertBefore(l.node, this.input);
  }

  setInput(open, text) {
    this.input.classList.toggle('on', open);
    if (open) this.inputText.textContent = text;
  }

  update(dt) {
    for (const l of this.chatLines) {
      if (l.life <= 0) continue;
      l.life -= dt;
      if (l.life <= 0) l.node.style.display = 'none';
    }
  }

  dispose() {
    this._nameplates.clear();
    this.root.remove();
  }
}
